const resources = {
  "en": {
    "accounts": {
      "title": "Manage Accounts",
      "pageDescription": "Keep active accounts ready for new activity, archive historical ones, and only delete truly empty accounts.",
      "summary": {
        "title": "Workspace account mix",
        "description": "Track how many accounts stay active, how much legacy inventory is archived, and where cards still need attention.",
        "totalRegistered": "Registered accounts",
        "totalRegisteredMeta": "{{active}} active · {{archived}} archived",
        "activeAccounts": "Active accounts",
        "activeAccountsMeta": "{{checking}} checking · {{savings}} savings · {{cash}} cash",
        "creditCards": "Credit cards",
        "creditCardsMeta": "{{vaults}} goal vaults active",
        "archivedAccounts": "Archived accounts",
        "archivedAccountsMeta": "Hidden from active planning and default account pickers."
      },
      "cardDescription": "Review balances, jump into account actions, and keep your active account order clean.",
      "add": "Add",
      "reorder": "Reorder",
      "done": "Done",
      "cardTitle": "Accounts",
      "mobileReorderTitle": "Reorder accounts on any screen",
      "mobileReorderDescription": "On mobile, tap \"Reorder\" to reveal move buttons. On larger screens, you can still drag accounts directly.",
      "mobileReorderActiveDescription": "Use the move buttons to shift accounts one position at a time. Each move saves immediately.",
      "moveUp": "Move account up",
      "moveDown": "Move account down",
      "menu": {
        "topup": "Top-up",
        "details": "Details",
        "balanceGraph": "Balance graph",
        "reconcile": "Reconcile",
        "undo": "Undo Reconcile",
        "edit": "Edit",
        "archive": "Archive",
        "restore": "Restore",
        "goals": "Goals",
        "deleteConfirm": "Delete this account?",
        "deleteOk": "Yes, delete",
        "deleteCancel": "Cancel",
        "archiveConfirm": "Archive this account and remove it from active use?",
        "archiveOk": "Archive",
        "archiveCancel": "Cancel"
      },
      "noReconcile": "No reconcile transaction to undo",
      "reconcileUndone": "✅ Reconciliation undone",
      "failedToLoad": "❌ Failed to load accounts",
      "accountUpdated": "✅ Account updated",
      "accountCreated": "✅ Account created",
      "accountDeleted": "✅ Account deleted",
      "accountArchived": "✅ Account archived",
      "accountRestored": "✅ Account restored",
      "cannotDeleteWithTx": "❌ Cannot delete account with transactions",
      "cannotDeleteWithLinks": "❌ Cannot delete account while it is still linked to recurring items, tasks, or debt records",
      "orderSaved": "✅ Order saved",
      "orderSaveFailed": "❌ Failed to save order",
      "errors": {
        "undoReconcile": "❌ Unable to undo reconcile: {{error}}",
        "save": "❌ Unable to save account: {{error}}",
        "delete": "❌ Unable to delete account: {{error}}",
        "archive": "❌ Unable to archive account: {{error}}",
        "restore": "❌ Unable to restore account: {{error}}",
        "unknown": "Unknown error"
      },
      "status": {
        "archived": "Archived"
      },
      "balance": "Balance",
      "availableToWithdraw": "Available to Withdraw",
      "noAccounts": "No accounts yet",
      "creditHealth": {
        "title": "Credit availability coach",
        "averageLabel": "Average available credit: {{percent}}",
        "status": {
          "good": "Great! Your cards have healthy available credit.",
          "warning": "You are on the edge—add payments to open up more credit.",
          "critical": "Utilization is high. Paying down cards will protect your score.",
          "none": ""
        },
        "targetHint": "Aim for at least {{target}}% average available credit.",
      "suggestion": {
        "title": "Suggested card to pay first",
        "description": "Pay {{amount}} on {{institution}} to lift the average to {{nextPercent}}."
      },
      "openCoach": "Open credit coach",
      "detailsTitle": "All cards",
      "metrics": {
        "limit": "Limit",
        "used": "Used",
        "available": "Available",
        "utilization": "Utilization"
      },
      "cardAvailable": "Available credit: {{percent}}",
      "cardAction": "Add {{amount}} to reach {{target}}% utilization.",
      "cardOnTrack": "Already at or below {{target}}% utilization."
      },
      "tabs": {
        "all": "All",
        "checking": "Checking",
        "savings": "Savings",
        "cash": "Cash",
        "credit": "Credit",
        "virtual": "Goals vaults",
        "archived": "Archived"
      },
      "balanceGraphModal": {
        "title": "📈 Balance history",
        "empty": "No balance data yet",
        "error": "Failed to load balance history"
      },
      "reconcileModal": {
        "title": "Reconcile Account",
        "dateLabel": "Date",
        "actualBalance": "Actual Balance",
        "currentDebt": "Current Debt",
        "availableCredit": "Available credit",
        "creditLimit": "Credit limit",
        "usedAtSetup": "Used at setup",
        "availableAtSetup": "Available at setup",
        "snapshotHint": "The values above are a historical snapshot from when this account was added.",
        "creditHint": "Enter the amount you currently owe on this credit account.",
        "annotationTitle": "Optional reporting annotation",
        "annotationBodyReady": "Reconciliation fixes ledger truth first. You can add a reporting-only category for this adjustment, or leave it unannotated and reports will keep it in review.",
        "annotationBodyPending": "Enter the current balance first. Once the adjustment direction is known, you can optionally add a reporting-only category.",
        "annotationLabel": "Reporting category",
        "annotationHint": "Optional. This does not change reconciliation semantics; it only annotates reports.",
        "annotationPlaceholder": "Select optional reporting category",
        "predictedDirectionIncome": "This reconciliation will create an income-side adjustment.",
        "predictedDirectionExpense": "This reconciliation will create an expense-side adjustment.",
        "messages": {
          "success": "✅ Adjustment: {{amount}}",
          "error": "❌ {{error}}",
          "unknownError": "Failed"
        }
      },
      "modal": {
        "editTitle": "Edit Account",
        "addTitle": "Add Account",
        "save": "Save",
        "institutionLabel": "Institution",
        "institutionRequired": "Institution is required",
        "institutionPlaceholder": "e.g. Bank of America",
        "typeLabel": "Type",
        "typeRequired": "Type is required",
        "typePlaceholder": "Select type",
        "typeChecking": "Checking",
        "typeSavings": "Savings",
        "typeCash": "Cash",
        "typeCredit": "Credit",
        "currency": "Currency",
        "currencyPlaceholder": "Select currency",
        "currencyRequired": "Currency is required",
        "assetType": "Asset type",
        "assetTypePlaceholder": "Optional asset type",
        "creditLimit": "Credit Limit",
        "usedLimit": "Used Limit",
        "usedLimitPlaceholder": "How much is already used?",
        "moneyHint": "Decimals are supported. Use a dot, for example: 12.34",
        "interestRate": "Interest Rate (%)",
        "dueDate": "Due Date (Day)",
        "dueDatePlaceholder": "e.g. 15",
        "notes": "Notes",
        "notesPlaceholder": "Any notes...",
        "optional": "Optional"
      }
    }
  },
  "uk": {
    "accounts": {
      "title": "Керування рахунками",
      "pageDescription": "Тримайте активні рахунки готовими до нових операцій, архівуйте історичні та видаляйте лише справді порожні рахунки.",
      "summary": {
        "title": "Мікс рахунків у workspace",
        "description": "Швидко бачте, скільки рахунків лишається активними, скільки вже в архіві, і де картки все ще потребують уваги.",
        "totalRegistered": "Усього рахунків",
        "totalRegisteredMeta": "{{active}} активних · {{archived}} в архіві",
        "activeAccounts": "Активні рахунки",
        "activeAccountsMeta": "{{checking}} розрахункових · {{savings}} накопичувальних · {{cash}} готівкових",
        "creditCards": "Кредитні картки",
        "creditCardsMeta": "{{vaults}} активних скарбничок цілей",
        "archivedAccounts": "Архівні рахунки",
        "archivedAccountsMeta": "Приховані з активного planning і стандартних списків вибору рахунків."
      },
      "cardDescription": "Переглядайте баланси, швидко відкривайте дії по рахунках і тримайте список активних рахунків охайним.",
      "add": "Додати",
      "reorder": "Змінити порядок",
      "done": "Готово",
      "cardTitle": "Рахунки",
      "mobileReorderTitle": "Порядок рахунків можна змінювати на будь-якому екрані",
      "mobileReorderDescription": "На мобільному натисніть «Змінити порядок», щоб побачити кнопки переміщення. На ширших екранах рахунки й далі можна перетягувати.",
      "mobileReorderActiveDescription": "Кнопки переміщують рахунок на одну позицію за раз. Кожна зміна зберігається одразу.",
      "moveUp": "Перемістити рахунок вище",
      "moveDown": "Перемістити рахунок нижче",
      "menu": {
        "topup": "Поповнити",
        "details": "Деталі",
        "balanceGraph": "Графік балансу",
        "reconcile": "Звірити",
        "undo": "Скасувати звірку",
        "edit": "Редагувати",
        "archive": "В архів",
        "restore": "Повернути",
        "goals": "Цілі",
        "deleteConfirm": "Видалити цей рахунок?",
        "deleteOk": "Так, видалити",
        "deleteCancel": "Скасувати",
        "archiveConfirm": "Архівувати цей рахунок і прибрати його з активного використання?",
        "archiveOk": "В архів",
        "archiveCancel": "Скасувати"
      },
      "noReconcile": "Немає звірки для скасування",
      "reconcileUndone": "✅ Звірку скасовано",
      "failedToLoad": "❌ Не вдалося завантажити рахунки",
      "accountUpdated": "✅ Рахунок оновлено",
      "accountCreated": "✅ Рахунок створено",
      "accountDeleted": "✅ Рахунок видалено",
      "accountArchived": "✅ Рахунок архівовано",
      "accountRestored": "✅ Рахунок відновлено",
      "cannotDeleteWithTx": "❌ Неможливо видалити рахунок з транзакціями",
      "cannotDeleteWithLinks": "❌ Неможливо видалити рахунок, поки він пов'язаний із рекурентними записами, задачами або боргами",
      "orderSaved": "✅ Порядок збережено",
      "orderSaveFailed": "❌ Не вдалося зберегти порядок",
      "errors": {
        "undoReconcile": "❌ Не вдалося скасувати звірку: {{error}}",
        "save": "❌ Не вдалося зберегти рахунок: {{error}}",
        "delete": "❌ Не вдалося видалити рахунок: {{error}}",
        "archive": "❌ Не вдалося архівувати рахунок: {{error}}",
        "restore": "❌ Не вдалося відновити рахунок: {{error}}",
        "unknown": "Невідома помилка"
      },
      "status": {
        "archived": "Архів"
      },
      "balance": "Баланс",
      "availableToWithdraw": "Доступно до зняття",
      "noAccounts": "Поки немає рахунків",
      "creditHealth": {
        "title": "Кредитний коуч",
        "averageLabel": "Середній доступний кредит: {{percent}}",
        "status": {
          "good": "Чудово! На картках достатньо доступного кредиту.",
          "warning": "На межі — додайте погашення, щоб відкрити кредит.",
          "critical": "Висока утилізація. Погашення допоможе захистити рейтинг.",
          "none": ""
        },
        "targetHint": "Прагніть щонайменше {{target}}% середнього доступного кредиту.",
        "suggestion": {
          "title": "Рекомендована карта для першого платежу",
          "description": "Сплатіть {{amount}} на {{institution}}, щоб підняти середній показник до {{nextPercent}}."
        },
        "openCoach": "Відкрити кредитний коуч",
        "detailsTitle": "Усі картки",
        "metrics": {
          "limit": "Ліміт",
          "used": "Використано",
          "available": "Доступно",
          "utilization": "Утилізація"
        },
        "cardAvailable": "Доступний кредит: {{percent}}",
        "cardAction": "Додайте {{amount}}, щоб досягти {{target}}% утилізації.",
        "cardOnTrack": "Вже на рівні {{target}}% або нижче."
      },
      "tabs": {
        "all": "Усі",
        "checking": "Розрахункові",
        "savings": "Накопичувальні",
        "cash": "Готівка",
        "credit": "Кредитні",
        "virtual": "Скарбнички цілей",
        "archived": "Архів"
      },
      "balanceGraphModal": {
        "title": "📈 Історія балансу",
        "empty": "Ще немає даних по балансу",
        "error": "Не вдалося завантажити історію балансу"
      },
      "reconcileModal": {
        "title": "Звірити рахунок",
        "dateLabel": "Дата",
        "actualBalance": "Фактичний баланс",
        "currentDebt": "Поточний борг",
        "availableCredit": "Доступний кредит",
        "creditLimit": "Кредитний ліміт",
        "usedAtSetup": "Використано при створенні",
        "availableAtSetup": "Доступно при створенні",
        "snapshotHint": "Значення вище — це історичний snapshot з моменту додавання рахунку.",
        "creditHint": "Вкажіть суму, яку ви зараз винні по цьому кредитному рахунку.",
        "annotationTitle": "Необовʼязкова reporting annotation",
        "annotationBodyReady": "Reconciliation спершу фіксує truth у ledger. Ви можете додати reporting-only категорію для цього adjustment або залишити його без анотації, і звіти чесно покажуть review state.",
        "annotationBodyPending": "Спочатку введіть поточний баланс. Коли напрямок adjustment стане зрозумілим, можна буде додати reporting-only категорію.",
        "annotationLabel": "Reporting category",
        "annotationHint": "Необовʼязково. Це не змінює reconciliation semantics, а лише додає annotation для звітів.",
        "annotationPlaceholder": "Оберіть необовʼязкову reporting category",
        "predictedDirectionIncome": "Ця звірка створить коригування з боку доходу.",
        "predictedDirectionExpense": "Ця звірка створить коригування з боку витрати.",
        "messages": {
          "success": "✅ Коригування: {{amount}}",
          "error": "❌ {{error}}",
          "unknownError": "Не вдалося виконати звірку"
        }
      },
      "modal": {
        "editTitle": "Редагувати рахунок",
        "addTitle": "Додати рахунок",
        "save": "Зберегти",
        "institutionLabel": "Установа",
        "institutionRequired": "Установа обов'язкова",
        "institutionPlaceholder": "наприклад, ПриватБанк",
        "typeLabel": "Тип",
        "typeRequired": "Тип обов'язковий",
        "typePlaceholder": "Оберіть тип",
        "typeChecking": "Розрахунковий",
        "typeSavings": "Накопичувальний",
        "typeCash": "Готівка",
        "typeCredit": "Кредитний",
        "currency": "Валюта",
        "currencyPlaceholder": "Оберіть валюту",
        "currencyRequired": "Валюта обов'язкова",
        "assetType": "Тип активу",
        "assetTypePlaceholder": "Необов'язковий тип активу",
        "creditLimit": "Кредитний ліміт",
        "usedLimit": "Використаний ліміт",
        "usedLimitPlaceholder": "Скільки вже використано?",
        "moneyHint": "Дробова частина підтримується. Використовуйте крапку, наприклад: 12.34",
        "interestRate": "Відсоткова ставка (%)",
        "dueDate": "День платежу",
        "dueDatePlaceholder": "напр. 15",
        "notes": "Нотатки",
        "notesPlaceholder": "Будь-які нотатки...",
        "optional": "Необов'язково"
      }
    }
  }
};

export default resources;
